import { Package } from "lucide-react";
import React from "react";

import { Card } from "@/components/ui/card";
import { formatMoney } from "@/lib/format";

type StockCardProps = {
  id: string;
  name: string;
  /** The API returns a nullable description. */
  description?: string | null;
  createdAt: string;
  kind?: string;
  currency?: string;
  monetaryValue?: number;
  onClick?: (id: string) => void;
};

export const StockCard: React.FC<StockCardProps> = ({
  id,
  name,
  description,
  createdAt,
  kind,
  currency,
  monetaryValue,
  onClick,
}) => {
  const isInteractive = Boolean(onClick);

  const handleClick = () => {
    onClick?.(id);
  };

  const handleKeyDown = (event: React.KeyboardEvent<HTMLDivElement>) => {
    if (!isInteractive || (event.key !== "Enter" && event.key !== " ")) return;

    event.preventDefault();
    handleClick();
  };

  return (
    <Card
      onClick={handleClick}
      onKeyDown={handleKeyDown}
      role={isInteractive ? "button" : undefined}
      tabIndex={isInteractive ? 0 : undefined}
      aria-label={isInteractive ? `Ouvrir le stock ${name}` : undefined}
      className={`w-full max-w-sm rounded-lg border p-0 shadow-sm transition ${
        isInteractive
          ? "cursor-pointer hover:shadow-md focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
          : ""
      }`}
    >
      <div className="p-4 flex items-start gap-3">
        <div className="shrink-0 rounded-3xl bg-brand-soft p-2 text-brand-black">
          <Package size={22} />
        </div>

        <div className="flex flex-col flex-1 min-w-0">
          <span className="text-sm font-medium text-muted-foreground">{kind ?? "Stock"}</span>
          <span className="font-semibold truncate text-base sm:text-lg">{name}</span>
          {description ? <span className="text-xs text-muted-foreground mt-1 line-clamp-2">{description}</span> : null}
          <span className="text-xs text-muted-foreground mt-2">Créé le {new Date(createdAt).toLocaleDateString()}</span>
        </div>
      </div>

      {typeof monetaryValue === "number" && currency ? (
        <div className="mt-2 flex flex-col justify-end items-end">
          <div className="text-sm font-semibold text-muted-foreground">Valeur monétaire</div>
          <div>
            <span className="text-2xl font-bold">{formatMoney(monetaryValue, currency)}</span>
          </div>
        </div>
      ) : null}
    </Card>
  );
};
